import React, { useEffect, useState } from "react";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { Box, Stack, Theme } from "@mui/material";
import { FilterModal } from "../FilterModal";
import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { ClientResponse, IPagination, IQueryPerams } from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import CustomizedSwitch from "../../components/Switch";
import { formatSnakeCaseToReadable } from "../../utils/common";
import { DatePicker } from "../../components/calender";
import dayjs from "dayjs";

interface FilterProps<T> {
  ftn: (params: IQueryPerams) => Promise<ClientResponse<T>>;
  updateQuerySet: (query: React.SetStateAction<IQueryPerams>) => void;
  updateDataSet: (data: T, pagination?: IPagination) => void;
}

interface Ifilter {
  batch_number: string;
  donor: string;
  received_date: string;
  is_deleted?: boolean;
}
export const BatchLogsFilter = <T,>({
  ftn,
  updateQuerySet,
  updateDataSet
}: FilterProps<T>) => {
  const [loading, setLoading] = useState(true);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<Ifilter>({
      batch_number: "",
      donor: "",
      received_date: ""
    });
  const resetFilter = <K extends keyof Ifilter>(key: K) => {
    updateForm((p) => {
      const value= typeof (p[key]?.value)=== 'boolean' ? false :""
      const state = {
        ...p,
        [key]: { value},
      };
      updateQuerySet((prev) => ({ ...prev, [key]: value }))
      return state;
    });
  };
  const handleDatePicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      received_date: { value: date, isValid: true }
    }));
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    updateQuerySet(data);
    const filterRequest = async () => {
      setLoading(true);
      const res = await ftn(data);
      if (res.data && updateDataSet) {
        updateDataSet(res.data, res.pagination);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.donor.value,
    form.received_date.value,
    form.is_deleted,
    form.batch_number.value
  ]);

  return (
    <Stack direction={"row"} justifyContent={"space-between"} width={"100%"}>
      <Stack direction={"row"} spacing={2}>
        <FilterModal label="Filters" loading={loading} closeOnoutSideClick>
          <Stack direction={"column"} spacing={2}>
            <DatePicker
              handleOnchange={handleDatePicker}
              label="Date Recieved"
              value={dayjs(form.received_date.value)}
            />
            <SideBarSearchInput
              label="Batch Number"
              placeholder="Search by batch number ..."
              value={form.batch_number.value}
              handleChange={updateInputField("batch_number")}
            />
            <CustomizedSwitch
              title="Deleted"
              onChange={updateInputField("is_deleted", "BOOLEAN")}
              value={form.is_deleted?.value ?? false}
            />
          </Stack>
        </FilterModal>
        <Box sx={styles.filterLayout}>
          {Object.entries(getFormValues())
            .filter(
              ([_, value]) =>
                (typeof value === "string" && value.trim() !== "") ||
                typeof value === "boolean"
            )
            .map(([key, value]) => (
              <FilterItemButton
                key={key}
                label={`By ${formatSnakeCaseToReadable(key)}`}
                tip={value.toString()}
                height={40}
                onClick={() => resetFilter(key as keyof Ifilter)}
              />
            ))}
        </Box>
      </Stack>
      <Box width={250}>
        <SideBarSearchInput
          placeholder="Search by Donor name..."
          value={form.donor.value}
          handleChange={updateInputField("donor")}
        />
      </Box>
    </Stack>
  );
};

const styles = {
  filterLayout: (theme: Theme) => ({
    width: 1000,
    [theme.breakpoints.down("xl")]: {
      width: 570
    }
  })
};
